import React, { useEffect, useState } from 'react';
import { 
  Card, 
  CardHeader, 
  CardBody, 
  Button, 
  Table, 
  Spinner, 
  Badge,
  Input,
  InputGroup,
  InputGroupText,
  Row,
  Col,
  UncontrolledTooltip,
  Alert,
} from 'reactstrap';
import { getInvoices } from '../../services/api/invoice-api';
import ProjectService from '../../services/api/projects-service';
import Moment from 'react-moment';
import Pagination from "react-js-pagination";

const InvoiceList = ({ projectId, onSelectInvoice, onGenerate, refreshTrigger }) => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [projectDetails, setProjectDetails] = useState(null);
  const [activePage, setActivePage] = useState(1);
  const [totalInvoices, setTotalInvoices] = useState(0);
  const [paidCount, setPaidCount] = useState(0);
  const RECORDS_PER_PAGE = 10;


  useEffect(() => {
    fetchInvoices();
  }, [projectId, refreshTrigger]);

  useEffect(() => {
    fetchProjectDetails();
  }, [projectId]);

  const fetchInvoices = async (filters = {}) => {
    const { search = searchTerm, status = statusFilter, page = activePage } = filters;

    if (!projectId) return;

    try {
      setLoading(true);
      setError(null);
      const res = await getInvoices({
        projectId,
        search: search || undefined,
        status: status !== 'all' ? status : undefined,
        page,
        limit: RECORDS_PER_PAGE,
      });
      setInvoices(res.data.invoices || []);
      setTotalInvoices(res.data.total || 0);
      setPaidCount(res.data.paidCount || 0);
    } catch (err) {
      setError('Failed to load invoices. Please try again.');
      console.error('Error fetching invoices:', err);
    } finally {
      setLoading(false);
    }
  };




  const fetchProjectDetails = async () => {
    if (!projectId) return;
    
    try {
      setLoading(true);
      setError(null);
      const res = await ProjectService.getProjectDetails(projectId);
      console.log('Project Details:', res);
      setProjectDetails(res.data || {});
    } catch (err) {
      setError('Failed to load project details. Please try again.');
      console.error('Error fetching project details:', err);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (pageNumber) => {
  setActivePage(pageNumber);
  fetchInvoices({ page: pageNumber });
};


  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'paid': return 'success';
      case 'sent': return 'info';
      case 'pending': return 'warning';
      case 'draft': return 'primary';
      case 'overdue': return 'danger';
      default: return 'secondary';
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'INR'
    }).format(amount || 0);
  };

  const getInvoiceStats = () => {
    const totalInvoices = invoices.length;
    const paidInvoices = invoices.filter(inv => inv.status === 'paid').length;
    const pendingInvoices = invoices.filter(inv => inv.status === 'pending').length;
    const totalAmount = invoices.reduce((sum, inv) => sum + (inv.totals?.total_amount || 0), 0);
    
    return { totalInvoices, paidInvoices, pendingInvoices, totalAmount };
  };

  const resetAndFetchInvoices = () => {
    setSearchTerm('');
    setStatusFilter('all');
    fetchInvoices({ search: '', status: 'all', page: 1 });
  };

  const stats = getInvoiceStats();

  if (loading) {
    return (
      <div className="content text-center py-5">
        <Spinner color="primary" size="lg" />
        <p className="mt-3 text-muted">Loading invoices...</p>
      </div>
    );
  }

  return (
    <>
      {/* Statistics Cards */}
      <Row className="mb-4">
        <Col lg="4" md="6">
          <Card className="card-stats">
            <CardBody>
              <Row>
                <Col xs="5">
                  <div className="icon-big text-center icon-primary">
                    <i className="nc-icon nc-layout-11 text-primary" />
                  </div>
                </Col>
                <Col xs="7">
                  <div className="numbers">
                    <p className="card-category">Project Name</p>
                    <p className="card-title text-sm" style={{ fontSize: "1.3rem" }}>{projectDetails?.name}</p>
                  </div>
                </Col>
              </Row>
            </CardBody>
          </Card>
        </Col>
        <Col lg="4" md="6">
          <Card className="card-stats">
            <CardBody>
              <Row>
                <Col xs="5">
                  <div className="icon-big text-center icon-warning">
                    <i className="nc-icon nc-paper text-warning" />
                  </div>
                </Col>
                <Col xs="7">
                  <div className="numbers">
                    <p className="card-category">Total Invoices</p>
                    <p className="card-title">{totalInvoices}</p>
                  </div>
                </Col>
              </Row>
            </CardBody>
          </Card>
        </Col>
        <Col lg="4" md="6">
          <Card className="card-stats">
            <CardBody>
              <Row>
                <Col xs="5">
                  <div className="icon-big text-center icon-success">
                    <i className="nc-icon nc-money-coins text-success" />
                  </div>
                </Col>
                <Col xs="7">
                  <div className="numbers">
                    <p className="card-category">Paid</p>
                    <p className="card-title">{paidCount}</p>
                  </div>
                </Col>
              </Row>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Main Invoice List Card */}
      <Card className="shadow">
        <CardHeader className="d-flex justify-content-between align-items-center">
          <div>
            <h4 className="mb-0">
              <i className="nc-icon nc-paper me-2" />
              Publisher Invoices
            </h4>
            <small className="text-muted">Manage and track all invoice payments</small>
          </div>
          <Button 
            color="primary" 
            onClick={onGenerate}
            className="btn-round"
          >
            <i className="nc-icon nc-simple-add me-1" />
            Generate New Invoice
          </Button>
        </CardHeader>
        
        <CardBody>
          {error && (
            <Alert color="danger" className="mb-3">
              <i className="nc-icon nc-simple-remove me-2" />
              {error}
              <Button 
                color="link" 
                size="sm" 
                onClick={fetchInvoices}
                className="ml-2"
              >
                Retry
              </Button>
            </Alert>
          )}

          {/* Filters */}
          <Row className="mb-3 align-items-center">
            <Col md="4">
            <div className="d-flex align-items-center h-100 pt-2">
              <InputGroup>
                <InputGroupText>
                  <i className="nc-icon nc-zoom-split" />
                </InputGroupText>
                <Input
                  placeholder="Search by invoice number or publisher..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </InputGroup>
            </div>
            </Col>

            <Col md="3">
              <Input
                type="select"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="all">All Status</option>
                <option value="draft">Draft</option>
                <option value="sent">Sent</option>
                <option value="pending">Pending</option>
                <option value="paid">Paid</option>
                <option value="overdue">Overdue</option>
              </Input>
            </Col>

            <Col md="5" className="d-flex gap-2">
              <Button color="primary" onClick={fetchInvoices} className="btn-round">
                <i className="nc-icon nc-zoom-split me-1" />
                Search
              </Button>
              <Button
                color="secondary"
                outline
                onClick={resetAndFetchInvoices}
                className="btn-round"
              >
                <i className="nc-icon nc-refresh-69 me-1" />
                Reset
              </Button>
            </Col>
          </Row>

          {totalInvoices > 0 && (
            <Row className="my-3 align-items-center">
              <Col md="6" className="text-right">
                <b>
                  {(activePage - 1) * RECORDS_PER_PAGE + 1} -{" "}
                  {Math.min(activePage * RECORDS_PER_PAGE, totalInvoices)} of {totalInvoices}
                </b>
              </Col>
              <Col md="6" className="text-left">
                <div className="d-inline-block">
                  <Pagination
                    activePage={activePage}
                    itemsCountPerPage={RECORDS_PER_PAGE}
                    totalItemsCount={totalInvoices}
                    pageRangeDisplayed={3}
                    onChange={handlePageChange}
                    itemClass="page-item"
                    linkClass="page-link"
                  />
                </div>
              </Col>
            </Row>
          )}



          {/* Invoice Table */}
          {invoices.length === 0 ? (
            <div className="text-center py-5">
              <i className="nc-icon nc-paper text-muted" style={{ fontSize: '4rem' }} />
              <h5 className="text-muted mt-3">
                {invoices.length === 0 ? 'No invoices found' : 'No invoices match your search'}
              </h5>
              <p className="text-muted">
                {invoices.length === 0 
                  ? 'Generate your first invoice to get started' 
                  : 'Try adjusting your search criteria'
                }
              </p>
              {invoices.length === 0 && (
                <Button color="primary" onClick={onGenerate} className="mt-2">
                  <i className="nc-icon nc-simple-add me-1" />
                  Generate Invoice
                </Button>
              )}
            </div>
          ) : (
            <div className="table-responsive">
              <Table hover className="tablesorter">
                <thead className="text-primary">
                  <tr>
                    <th>#</th>
                    <th>Invoice No.</th>
                    {/* <th>Publisher</th> */}
                    <th>Period</th>
                    <th>Status</th>
                    <th>Total</th>
                    <th>Created</th>
                    <th>Generated By</th>
                    <th className="text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((invoice, index) => (
                    <tr key={invoice._id || index}>
                      <td>
                        {(activePage - 1) * RECORDS_PER_PAGE + index + 1}
                      </td>
                      <td>
                        <strong>{invoice?.invoice_id || invoice?.invoice_number}</strong>
                      </td>
                      {/* <td>
                        <div>
                          <span className="font-weight-bold">
                            {invoice.publisher_id?.name || invoice.publisher_id}
                          </span>
                          {invoice.publisher_id?.email && (
                            <div className="text-muted small">
                              {invoice.publisher_id.email}
                            </div>
                          )}
                        </div>
                      </td> */}
                      <td>
                        <div className="text-sm">
                          {invoice.invoice_period?.start_date && (
                            <>
                              {new Date(invoice.invoice_period.start_date).toLocaleDateString('en-GB', {
                                day: 'numeric',
                                month: 'long',
                                year: 'numeric'
                              })}
                              <span className="text-muted mx-2">-</span>
                              {new Date(invoice.invoice_period.end_date).toLocaleDateString('en-GB', {
                                day: 'numeric',
                                month: 'long',
                                year: 'numeric'
                              })}
                            </>
                          )}
                        </div>
                      </td>
                      <td>
                        <Badge 
                          color={getStatusBadgeColor(invoice.status)}
                          className="text-capitalize"
                        >
                          {invoice.status}
                        </Badge>
                      </td>
                      <td>
                        <strong className="text-success">
                          {formatCurrency(invoice.totals?.total_amount || ((invoice.totals?.subtotal)*1.18-(invoice.totals?.subtotal)*0.02))}
                        </strong>
                      </td>
                      <td className="text-muted text-sm">
                        {invoice.createdAt? <Moment format="DD MMM YYYY hh:mm:ss a">{invoice.createdAt}</Moment> : ''}
                      </td>
                      <td className="text-muted text-sm">
                        {invoice.generated_by? invoice.generated_by : ''}
                      </td>
                      <td className="text-center">
                        <Button
                          color="info"
                          size="sm"
                          onClick={() => onSelectInvoice(invoice._id)}
                          id={`tooltip-${invoice._id}`}
                          className="btn-round btn-icon"
                        >
                          <i className="nc-icon nc-zoom-split" />
                        </Button>
                        <UncontrolledTooltip 
                          placement="top" 
                          target={`tooltip-${invoice._id}`}
                        >
                          View Invoice Details
                        </UncontrolledTooltip>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </CardBody>
      </Card>
    </>
  );
};

export default InvoiceList;